import { useEffect, useState } from 'react';
import { Modal } from "react-bootstrap";
import { useDispatch } from 'react-redux';
import toast from "react-hot-toast";
import { Helpers } from '../../../../helpers/Helpers';
import { AmbarFindPriceRequest } from '../../../../models/request/ambar/AmbarFindPriceRequest';
import { GetAmbarListResponse, GetAmbarListResponseAmbarExpectedIncomeOutcome, GetAmbarListResponsePriceCandidate } from '../../../../models/response/ambar/GetAmbarListResponse';
import { storeActivityIndicator } from '../../../../redux/reducers/authSlice';
import { AmbarService } from '../../../../services/AmbarService';
import { PriceGroupDecisionEnum } from '../../../../models/common/Enums';
import { fiyatBulGuncellenenSatirlar, fiyatBulYeniSatirlar } from './fiyatBulYeniSatirlar';
import PriceGroupDecisionModal from '../../AmbarFindPrice/Components/PriceGroupDecisionModal';
import SeferUyeleri from '../../AmbarFindPrice/Components/SeferUyeleri';
import { kmAciklamasiGoster, kmBirimBilgisi, kmBirimSatiriMi, kmBirimBilgisiMetni, seferdeYazilanSayisi, seferUyeSatirlari, SeferUyeSatiri } from '../../../../helpers/seferFiyat';

interface Props {
    isShown: boolean
    ambarId: string | undefined
    isIncome: boolean
    /** Ekranda hâlihazırda duran satırların kimlikleri; yalnız yeni eklenenler geri verilir. */
    mevcutSatirIdleri: string[]
    onClose: () => void
    /** guncellenenler: sunucunun ekrandaki satiri guncelledigi durum (sefer satiri). */
    onSaved: (yeniSatirlar: GetAmbarListResponseAmbarExpectedIncomeOutcome[], guncellenenler: GetAmbarListResponseAmbarExpectedIncomeOutcome[]) => void
}

/**
 * Siparişin kendi ekranından fiyat bulma.
 *
 * Toplu Fiyat Bul ekranındaki işin aynısını tek sipariş için yapar: uyan anlaşmalardan
 * gelen satırlar listelenir, kullanıcı seçtiklerini kaydeder ve satırlar hem Beklenen
 * Gelir tablosuna eklenir hem de siparişe yazılır — aşağıdaki Kaydet'e gerek kalmaz.
 *
 * Uyan anlaşma yoksa, en yakın anlaşmalar ve tutmayan alanları gösterilir; böylece
 * kullanıcı eksiğin nerede olduğunu (çoğu zaman girilmemiş bir tarife satırı) görür.
 */
const FindPriceModal = ({ isShown, ambarId, isIncome, mevcutSatirIdleri, onClose, onSaved }: Props) => {
    const dispatch = useDispatch();
    const [satirlar, setSatirlar] = useState<GetAmbarListResponseAmbarExpectedIncomeOutcome[]>([]);
    const [adaylar, setAdaylar] = useState<GetAmbarListResponsePriceCandidate[]>([]);
    const [neden, setNeden] = useState<string | undefined>(undefined);
    const [baslik, setBaslik] = useState<string>('');
    const [yukleniyor, setYukleniyor] = useState(false);
    // Sefer Fiyat Bul: ayni seferin diger siparisleri (bilgi amacli, Kaydet'te sunucu otomatik yazar).
    const [seferUyeleri, setSeferUyeleri] = useState<SeferUyeSatiri[]>([]);
    // Toplama - Dagitim KM zinciri: "KM birim fiyatı: {fiyat} ÷ {km1} km = {kmBirim} TRY/km" bilgisi (zincir yoksa bos).
    const [kmBilgisi, setKmBilgisi] = useState<string>('');
    // Günlük Bölüm Grubu kararı (bkz. kararGerekli).
    const [isDecisionShown, setIsDecisionShown] = useState(false);
    const [conflictRefNos, setConflictRefNos] = useState<string[]>([]);
    const [pendingAction, setPendingAction] = useState<'bul' | 'kaydet' | undefined>(undefined);
    const [groupDecision, setGroupDecision] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (isShown && ambarId)
            fiyatBul(ambarId);
    }, [isShown, ambarId])

    /**
     * Günlük Bölüm Grubu: sunucu, aynı gün aynı gruptaki önceki (faturalanmamış) siparişler için karar isterse
     * pencere açılır ve true döner; hesap/kayıt yapılmaz. Karar verilince aynı işlem kararla tekrarlanır.
     */
    const kararGerekli = (response: GetAmbarListResponse, islem: 'bul' | 'kaydet') => {
        if (!response.data.some(x => x.priceGroupDecisionNeeded))
            return false;

        setConflictRefNos(response.data.filter(x => x.isPriceGroupConflict).map(x => x.refNo));
        setPendingAction(islem);
        setIsDecisionShown(true);

        return true;
    }

    const kararVerildi = (decision: string) => {
        const islem = pendingAction;

        setIsDecisionShown(false);
        setPendingAction(undefined);

        if (islem == 'bul' && ambarId)
            fiyatBul(ambarId, decision);
        else if (islem == 'kaydet')
            kaydet(decision);
    }

    const kararIptal = () => {
        setIsDecisionShown(false);
        setPendingAction(undefined);
    }

    const fiyatBul = async (_ambarId: string, decision?: string) => {
        try {
            setYukleniyor(true);
            setSatirlar([]);
            setAdaylar([]);
            setNeden(undefined);
            setSeferUyeleri([]);
            setKmBilgisi('');

            dispatch(storeActivityIndicator(true));

            var obj: AmbarFindPriceRequest = {
                isIncome: isIncome,
                isTemp: true,
                groupDecision: decision,
                // Elle basilan Fiyat Bul hesaplanmis seferi de yeniden hesaplar.
                hesaplanmisSeferleriAtla: false,
                details: [{ ambarId: _ambarId }]
            }

            var response: GetAmbarListResponse = await AmbarService.ambarFindPrice(obj);

            dispatch(storeActivityIndicator(false));
            setYukleniyor(false);

            if (!response.isSuccess) {
                toast.error(response.message)
                return;
            }

            if (kararGerekli(response, 'bul'))
                return;

            setGroupDecision(decision);

            // Karar sonrası yanıtta önceki siparişler de gelir; bu pencere yalnız kendi siparişini gösterir.
            var siparis = response.data.find(x => x.ambarId == _ambarId) ?? (response.data.length > 0 ? response.data[0] : undefined);

            if (!siparis)
                return;

            setBaslik([
                siparis.refNo,
                siparis.customerName,
                [siparis.loadingCountyName, siparis.loadingDistrictName].filter(x => x).join('/') + ' → ' +
                [siparis.deliverCountyName, siparis.deliverDistrictName].filter(x => x).join('/'),
                siparis.ambarVoyageRefNo ? 'Sefer ' + siparis.ambarVoyageRefNo : ''
            ].filter(x => x).join('  ·  '));

            setSeferUyeleri(seferUyeSatirlari(response.data.filter(x => x.seferdenEklendi && x.ambarId != siparis!.ambarId)));

            // 1. siparis bu siparis de olabilir, seferin baska bir siparisi de: ikisine birden bakilir.
            var km = kmBirimBilgisi([siparis, ...response.data.filter(x => x.seferdenEklendi && x.ambarId != siparis!.ambarId)]);
            setKmBilgisi(km ? kmBirimBilgisiMetni(km) + (km.refNo && km.refNo != siparis.refNo ? ' (' + km.refNo + ')' : '') : '');

            // Fiyatı 0 olan anlaşmalar da listelenir; tutarı sıfır olan hizmet de eklenebilmelidir.
            // Satır kimliği sipariş + şablon (toplu Fiyat Bul ile aynı; kaydetme de bu çiftle yapılır). Sunucu kimlik
            // göndermiyordu: hepsi boş kaldığından tek satıra tıklayınca bütün satırlar seçiliyordu (06.10.2026).
            setSatirlar(siparis.ambarExpectedIncomeOutcomes.filter(x => x.agreementSchemeId).map(function (x) {
                x.isSelected = false;
                x.uniqueId = (siparis!.ambarId ?? _ambarId) + "-" + x.agreementSchemeId;
                return x;
            }));

            setAdaylar(siparis.priceCandidates ?? []);
            setNeden(siparis.priceNotFoundReason);
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
            setYukleniyor(false);
        }
    }

    const tumunuSec = () => {
        var hepsiSecili = satirlar.length == satirlar.filter(x => x.isSelected).length;

        setSatirlar(satirlar.map(function (x) {
            x.isSelected = !hepsiSecili;
            return x;
        }));
    }

    const satirSec = (_uniqueId: string) => {
        setSatirlar(satirlar.map(function (x) {
            if (x.uniqueId == _uniqueId)
                x.isSelected = !x.isSelected;

            return x;
        }));
    }

    const kaydet = async (decision?: string) => {
        var secilenler = satirlar.filter(x => x.isSelected);
        // Bu sipariste fiyat yoksa da seferdeki diger siparislerin fiyati yazilabilir.
        var uyeYazilacak = seferUyeleri.some(x => x.sonucTipi == 'yazilacak');

        if (secilenler.length == 0 && !uyeYazilacak) {
            toast.error("En az bir satır seçiniz!");
            return
        }

        if (!ambarId)
            return;

        try {
            dispatch(storeActivityIndicator(true));

            var obj: AmbarFindPriceRequest = {
                isIncome: isIncome,
                isTemp: false,
                // Fiyat bulunurken verilen karar kaydederken de geçerlidir.
                groupDecision: decision ?? groupDecision,
                hesaplanmisSeferleriAtla: false,
                // Yalniz bu siparisin secili ciftleri; sefer uyelerini sunucu otomatik yazar.
                // Bu siparisten satir secilmediyse siparis yine gonderilir (sefer genisletmesi icin); sunucu onun satirini yazmaz.
                details: secilenler.length > 0
                    ? secilenler.map(function (x) {
                        return {
                            ambarId: ambarId,
                            agreementSchemeId: x.agreementSchemeId
                        }
                    })
                    : [{ ambarId: ambarId }]
            }

            var response: GetAmbarListResponse = await AmbarService.ambarFindPrice(obj);

            dispatch(storeActivityIndicator(false));

            if (!response.isSuccess) {
                toast.error(response.message)
                return;
            }

            if (kararGerekli(response, 'kaydet'))
                return;

            // Yazılan satırların gerçek kimlikleri yanıttan gelir; ekrana yalnız YENİ olanlar
            // eklenir. Aynı hizmet ve tutar zaten varsa sunucu mevcut satırı günceller,
            // o zaman yeni kimlik olmaz ve listeye bir şey eklenmez.
            var siparis = response.data.find(x => x.ambarId == ambarId) ?? (response.data.length > 0 ? response.data[0] : undefined);

            // Yanıtta seçilmeyen şablonların kaydedilmemiş aday satırları da gelir (kimlik boş: 00000000-…).
            // Yalnız seçilen şablonların gerçekten yazılan satırları eklenir (09.10.2026: tek satır seçilince iki satır ekleniyordu).
            var secilenSemalar = secilenler.map(x => x.agreementSchemeId);

            var yeniSatirlar = fiyatBulYeniSatirlar(siparis?.ambarExpectedIncomeOutcomes ?? [], secilenSemalar, mevcutSatirIdleri);
            var guncellenenler = fiyatBulGuncellenenSatirlar(siparis?.ambarExpectedIncomeOutcomes ?? [], secilenSemalar, mevcutSatirIdleri);

            onSaved(yeniSatirlar, guncellenenler);

            var uyeSayisi = seferdeYazilanSayisi(response.data, ambarId);

            toast.success(uyeSayisi > 0 ? "Fiyat siparişe ve seferdeki " + uyeSayisi + " siparişe eklendi." : "Fiyat siparişe eklendi.");
            onClose();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    return (
        <>
        <Modal
            tabIndex={-1}
            aria-hidden='true'
            dialogClassName='modal-dialog modal-dialog-centered mw-1200px'
            show={isShown}
            onHide={onClose}
        >
            <div className='modal-header'>
                <div>
                    <h2 className='mb-1'>Fiyat Bul — Beklenen {isIncome ? 'Gelir' : 'Gider'}</h2>
                    <div className='text-muted fw-semibold' style={{ fontSize: 12 }}>{baslik}</div>
                </div>
            </div>

            <div className='modal-body'>
                {
                    kmBilgisi && (
                        <div className='alert alert-primary d-flex align-items-start p-4 mb-5'>
                            <i className='fa fa-info-circle text-primary fs-3 me-3 mt-1'></i>
                            <div className='fs-7'>{kmBilgisi}</div>
                        </div>
                    )
                }

                {
                    satirlar.length > 0 && (
                        <div className='table-responsive'>
                            <table className='table align-middle dataTable no-footer fiyat-bul-tablo'>
                                <thead>
                                    <tr className='text-start fw-bolder text-uppercase'>
                                        <th style={{ width: 1 }}>
                                            <div className='d-flex'>
                                                <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                    <input onChange={tumunuSec} className='form-check-input' type='checkbox' checked={satirlar.filter(x => x.isSelected).length > 0 && satirlar.filter(x => x.isSelected).length == satirlar.length} />
                                                </label>
                                            </div>
                                        </th>
                                        <th>Hizmet Kalemi</th>
                                        <th className='fiyat-bul-sayi'>Birim Fiyat</th>
                                        <th className='fiyat-bul-sayi'>Miktar</th>
                                        <th className='fiyat-bul-sayi'>KDV %</th>
                                        <th className='fiyat-bul-sayi'>Toplam Tutar</th>
                                        <th className='fiyat-bul-sayi'>KDV Tutar</th>
                                        <th className='fiyat-bul-sayi'>Brüt Tutar</th>
                                        <th>Açıklama</th>
                                    </tr>
                                </thead>
                                <tbody className='text-gray-600 fw-bold'>
                                    {
                                        satirlar.map((item, index) =>
                                            <tr key={index} className={item.isSelected ? 'fiyat-bul-secili' : undefined}>
                                                <td style={{ width: 1 }}>
                                                    <div className='d-flex'>
                                                        <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                            <input onChange={() => satirSec(item.uniqueId)} className='form-check-input' type='checkbox' checked={item.isSelected} />
                                                        </label>
                                                    </div>
                                                </td>
                                                <td>{item.serviceName}</td>
                                                <td className='fiyat-bul-sayi'>{Helpers.correctPrice(item.unitPrice, kmBirimSatiriMi(item) ? 4 : 2)}</td>
                                                <td className='fiyat-bul-sayi'>{Helpers.correctPrice(item.quantity)}</td>
                                                <td className='fiyat-bul-sayi'>{item.taxRate}</td>
                                                <td className='fiyat-bul-sayi'>{Helpers.correctPrice(item.totalWithoutTaxPrice, 2)}</td>
                                                <td className='fiyat-bul-sayi'>{Helpers.correctPrice(item.taxPrice, 4)}</td>
                                                <td className='fiyat-bul-sayi'>{Helpers.correctPrice(item.totalPrice, 2)}</td>
                                                <td>
                                                    {item.agreementSchemeName}
                                                    {
                                                        // Mesai Detay Saat: aynı tutardaki kişi satırları açıklamadan ayırt edilir.
                                                        item.description && (
                                                            <div className='text-muted fw-normal' style={{ fontSize: 12 }}>{item.description}</div>
                                                        )
                                                    }
                                                    {
                                                        // Toplama - Dagitim KM zinciri: KM hesabi (aciklamada zaten yaziyorsa tekrar edilmez).
                                                        kmAciklamasiGoster(item) && (
                                                            <div className='text-muted fw-normal' style={{ fontSize: 12 }}>{kmAciklamasiGoster(item)}</div>
                                                        )
                                                    }
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>

                            <div className='text-muted fw-semibold mt-3' style={{ fontSize: 12 }}>
                                Yalnız seçilen satırlar kaydedilir; ekrandaki diğer değişiklikler için Kaydet'e basmanız gerekir.
                            </div>
                        </div>
                    )
                }

                {
                    !yukleniyor && satirlar.length == 0 && (
                        <div className='alert alert-warning d-flex align-items-center p-4 mb-5'>
                            <i className="fa fa-exclamation-triangle text-warning me-3" style={{ fontSize: 18 }} />
                            <div className='fw-semibold' style={{ fontSize: 13 }}>{neden ?? 'Bu siparişe uyan anlaşma bulunamadı.'}</div>
                        </div>
                    )
                }

                {
                    satirlar.length == 0 && adaylar.length > 0 && (
                        <div>
                            <div className='fw-bolder text-gray-800 mb-3' style={{ fontSize: 13 }}>En yakın anlaşmalar</div>

                            <div className='table-responsive'>
                                <table className='table align-middle dataTable no-footer fiyat-bul-tablo'>
                                    <thead>
                                        <tr className='text-start fw-bolder text-uppercase'>
                                            <th style={{ minWidth: 150 }}>Anlaşma Şablonu</th>
                                            <th style={{ minWidth: 150, whiteSpace: 'nowrap' }}>Anlaşma No</th>
                                            <th style={{ width: 90 }} className='text-center fiyat-bul-sayi'>Tutmayan</th>
                                            <th>Eşleşmeyen Alanlar</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-600 fw-bold'>
                                        {
                                            adaylar.map((aday, sira) =>
                                                <tr key={aday.agreementRefNo + '-' + sira}>
                                                    <td>{aday.schemeName}</td>
                                                    <td>{aday.agreementRefNo}</td>
                                                    <td className='text-center'>
                                                        <span className={'badge ' + (aday.mismatchCount <= 1 ? 'badge-light-warning' : 'badge-light-danger')}>
                                                            {aday.mismatchCount}
                                                        </span>
                                                    </td>
                                                    <td style={{ whiteSpace: 'normal' }}>
                                                        {
                                                            aday.mismatches.slice(0, 3).map((alan, i) =>
                                                                <span key={i} className='badge badge-light-danger me-2 mb-1 fw-normal'>
                                                                    <span className='fw-bold'>{alan.fieldName}</span>
                                                                    &nbsp;— anlaşmada: {alan.expectedValue} · siparişte: {alan.actualValue}
                                                                </span>
                                                            )
                                                        }
                                                        {
                                                            aday.mismatches.length > 3 && (
                                                                <span className='badge badge-light me-2 mb-1 fw-normal'>
                                                                    +{aday.mismatches.length - 3} alan daha
                                                                </span>
                                                            )
                                                        }
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                }

                {
                    seferUyeleri.length > 0 && (
                        <div className='mt-8'>
                            <div className='fw-bolder text-gray-800 mb-3' style={{ fontSize: 13 }}>
                                Bu sefere bağlı diğer siparişler
                                <span className='text-muted fw-semibold ms-2'>(bilgi amaçlı · Kaydet ile birlikte yazılır)</span>
                            </div>

                            <SeferUyeleri satirlar={seferUyeleri} pencere />
                        </div>
                    )
                }
            </div>

            <div className="modal-footer">
                <button className="btn btn-sm btn-warning me-3" onClick={onClose}>
                    <i className="fa fa-times" /> İptal Et
                </button>

                {
                    (satirlar.length > 0 || seferUyeleri.some(x => x.sonucTipi == 'yazilacak')) && (
                        <button className="btn btn-sm btn-success" onClick={() => kaydet()}>
                            <i className="fa fa-save" /> Kaydet
                        </button>
                    )
                }
            </div>
        </Modal>

        <PriceGroupDecisionModal
            isShown={isDecisionShown}
            refNos={conflictRefNos}
            onSeparate={() => kararVerildi(PriceGroupDecisionEnum.SEPARATE)}
            onTogether={() => kararVerildi(PriceGroupDecisionEnum.TOGETHER)}
            onClose={kararIptal}
        />
        </>
    )
}

export default FindPriceModal;
